import { API_URL, apiFetch } from '../../api';

/**
 * API för modulinstruktionerna (biblioteket /instruktioner och hjälpvyn).
 * Backend: backend/app/routers/instruktioner.py. Behörigheten avgörs i
 * backend — listan innehåller bara instruktioner användaren får se.
 */

const BAS = `${API_URL}/api/instruktioner`;

async function felText(res, standard) {
  try {
    const body = await res.json();
    if (body && typeof body.detail === 'string') return body.detail;
  } catch { /* tomt svar */ }
  return standard;
}

export async function listaInstruktioner() {
  const res = await apiFetch(BAS);
  if (!res.ok) throw new Error(await felText(res, 'Kunde inte hämta instruktionerna'));
  return res.json();
}

export async function sokInstruktioner(fraga) {
  const res = await apiFetch(`${BAS}/sok?q=${encodeURIComponent(fraga)}`);
  if (!res.ok) throw new Error(await felText(res, 'Sökningen misslyckades'));
  return (await res.json()).traffar || [];
}

/**
 * @param {string} fraga
 * @param {string|null} nyckel  instruktionen användaren tittar på
 * @param {{titel: string, text: string}|null} [beskrivning]  modulbeskrivningen när modulen saknar instruktion
 */
export async function fragaKanai(fraga, nyckel, beskrivning = null) {
  const res = await apiFetch(`${BAS}/fraga`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fraga, nyckel, beskrivning }),
  });
  if (!res.ok) throw new Error(await felText(res, 'KanAI kunde inte svara just nu'));
  return res.json();
}

// Hämtade dokument sparas för sessionen, så att en instruktion som redan
// visats öppnas direkt (byte av flik, dölj KanAI, bläddra tillbaka).
const dokumentCache = new Map();

export function harDokument(nyckel) {
  return dokumentCache.has(nyckel);
}

/**
 * Hämtar instruktionens HTML. onForlopp(0–100) anropas medan filen hämtas,
 * räknat på Content-Length när servern skickar det.
 * @param {string} nyckel
 * @param {(procent: number) => void} [onForlopp]
 * @returns {Promise<string>}
 */
export async function hamtaDokument(nyckel, onForlopp) {
  if (dokumentCache.has(nyckel)) return dokumentCache.get(nyckel);
  const res = await apiFetch(`${BAS}/${encodeURIComponent(nyckel)}/dokument`);
  if (!res.ok) throw new Error(await felText(res, 'Instruktionen kunde inte hämtas'));
  const totalt = Number(res.headers.get('content-length')) || 0;
  let html;
  if (!res.body || !totalt || !onForlopp) {
    html = await res.text();
  } else {
    const lasare = res.body.getReader();
    const delar = [];
    let mottaget = 0;
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await lasare.read();
      if (done) break;
      delar.push(value);
      mottaget += value.length;
      // Komprimerad överföring kan ge fler byte än Content-Length — stanna under 100.
      onForlopp(Math.min(95, Math.round((mottaget / totalt) * 100)));
    }
    const allt = new Uint8Array(mottaget);
    let pos = 0;
    for (const d of delar) { allt.set(d, pos); pos += d.length; }
    html = new TextDecoder('utf-8').decode(allt);
  }
  dokumentCache.set(nyckel, html);
  return html;
}
